/**
 * 图层面板（由 Vue LayerProperties 迁移）
 * 图层列表、顺序调整、显示/隐藏、锁定、对齐与分布
 */

import { Button, ButtonGroup, Label } from '@heroui/react';
import {
  IconArrowDown as ArrowDownToLine,
  IconArrowUp as ArrowUpToLine,
  IconChevronDown as ChevronDown,
  IconChevronUp as ChevronUp,
  IconLayoutAlignBottom,
  IconLayoutAlignCenter,
  IconLayoutAlignLeft,
  IconLayoutAlignMiddle,
  IconLayoutAlignRight,
  IconLayoutAlignTop,
  IconLayoutDistributeHorizontal,
  IconLayoutDistributeVertical,
  IconEye as Eye,
  IconEyeOff as EyeOff,
  IconLock as Lock,
  IconLockOpen as Unlock,
} from '@tabler/icons-react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { Element } from '@/components/canvas/document/publicTypes';
import { resolveGroupableRootIds } from '@/components/canvas/workspace/model/contextMenuSelection';

const ELEMENT_TYPE_LABEL: Record<string, string> = {
  text: '文本',
  image: '图片',
  line: '直线',
  rect: '矩形',
  circle: '圆形',
  triangle: '三角形',
  group: '组',
};

export function LayerProperties() {
  const {
    getCurrentPage,
    selectedElementIds,
    selectElement,
    updateElement,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
    alignSelectedTop,
    alignSelectedBottom,
    alignSelectedLeft,
    alignSelectedRight,
    alignSelectedCenterH,
    alignSelectedCenterV,
    distributeSpacingH,
    distributeSpacingV,
    groupElements,
    ungroupElement,
  } = useCanvas();

  const page = getCurrentPage();
  const elements = page?.elements ?? [];
  const sorted = [...elements].sort((a, b) => a.layout.zIndex - b.layout.zIndex);
  const canArrange = selectedElementIds.length > 0;
  const canAlign = selectedElementIds.length >= 2;
  const canDistribute = selectedElementIds.length >= 3;
  const groupableRootIds = resolveGroupableRootIds(elements, selectedElementIds);
  const canGroup = groupableRootIds.length >= 2;
  const selectedOne =
    selectedElementIds.length === 1
      ? elements.find((e) => e.id === selectedElementIds[0])
      : null;
  const canUngroup = selectedOne?.type === 'group';

  const handleToggleVisibility = (el: Element) => {
    updateElement(el.id, { interaction: { visible: el.interaction.visible === false } });
  };

  const handleToggleLock = (el: Element) => {
    updateElement(el.id, { interaction: { locked: !el.interaction.locked } });
  };

  return (
    <div className="property-panel-view flex min-h-0 w-full shrink-0 flex-col gap-4 p-4">
      {/* 对齐 */}
      <div>
        <Label className="text-xs text-muted">对齐</Label>
        <div className="mt-1 flex flex-nowrap items-center gap-1.5">
          <ButtonGroup orientation="horizontal" className="shrink-0">
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canAlign}
              aria-label="左对齐"
              onPress={alignSelectedLeft}
            >
              <IconLayoutAlignLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canAlign}
              aria-label="右对齐"
              onPress={alignSelectedRight}
            >
              <IconLayoutAlignRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canAlign}
              aria-label="顶对齐"
              onPress={alignSelectedTop}
            >
              <IconLayoutAlignTop className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canAlign}
              aria-label="底对齐"
              onPress={alignSelectedBottom}
            >
              <IconLayoutAlignBottom className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canAlign}
              aria-label="水平居中对齐"
              onPress={alignSelectedCenterV}
            >
              <IconLayoutAlignMiddle className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canAlign}
              aria-label="垂直居中对齐"
              onPress={alignSelectedCenterH}
            >
              <IconLayoutAlignCenter className="h-4 w-4" />
            </Button>
          </ButtonGroup>
          <ButtonGroup orientation="horizontal" className="shrink-0">
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canDistribute}
              aria-label="水平均匀间距"
              onPress={distributeSpacingH}
            >
              <IconLayoutDistributeHorizontal className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              isIconOnly
              size="sm"
              isDisabled={!canDistribute}
              aria-label="垂直均匀间距"
              onPress={distributeSpacingV}
            >
              <IconLayoutDistributeVertical className="h-4 w-4" />
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* 图层顺序 */}
      <div>
        <Label className="text-xs text-muted">图层顺序</Label>
        <div className="mt-1 flex flex-wrap gap-1">
          <Button
            variant="ghost"
            isIconOnly
            size="sm"
            isDisabled={!canArrange}
            aria-label="置于顶层"
            onPress={() => bringToFront()}
          >
            <ArrowUpToLine className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            isIconOnly
            size="sm"
            isDisabled={!canArrange}
            aria-label="上移一层"
            onPress={() => bringForward()}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            isIconOnly
            size="sm"
            isDisabled={!canArrange}
            aria-label="下移一层"
            onPress={() => sendBackward()}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            isIconOnly
            size="sm"
            isDisabled={!canArrange}
            aria-label="置于底层"
            onPress={() => sendToBack()}
          >
            <ArrowDownToLine className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 编组 / 解组 */}
      <div>
        <Label className="text-xs text-muted">编组</Label>
        <div className="mt-1 flex flex-wrap gap-1">
          <Button
            variant="outline"
            size="sm"
            isDisabled={!canGroup}
            onPress={() => groupElements(groupableRootIds)}
          >
            编组
          </Button>
          <Button
            variant="outline"
            size="sm"
            isDisabled={!canUngroup}
            onPress={() => selectedOne && ungroupElement(selectedOne.id)}
          >
            解组
          </Button>
        </div>
      </div>

      {/* 图层列表 */}
      <div>
        <Label className="text-xs text-muted">图层列表</Label>
        <div className="mt-1 rounded-lg border border-divider">
          {sorted.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted">暂无图层</div>
          ) : (
            <ul className="divide-y divide-divider">
              {[...sorted].reverse().map((el) => {
                const selected = selectedElementIds.includes(el.id);
                const visible = el.interaction.visible !== false;
                const locked = el.interaction.locked === true;
                return (
                  <li
                    key={el.id}
                    className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-default-200/60 ${
                      selected ? 'bg-primary/10' : ''
                    }`}
                    onClick={() => selectElement(el.id, false)}
                  >
                    <span className="flex-1 truncate" title={el.meta.name ?? el.type}>
                      {el.meta.name ?? ELEMENT_TYPE_LABEL[el.type] ?? el.type}
                    </span>
                    <button
                      type="button"
                      className="rounded p-0.5 hover:bg-default-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleVisibility(el);
                      }}
                      title={visible ? '隐藏' : '显示'}
                    >
                      {visible ? (
                        <Eye className="h-3.5 w-3.5" />
                      ) : (
                        <EyeOff className="h-3.5 w-3.5 opacity-50" />
                      )}
                    </button>
                    <button
                      type="button"
                      className="rounded p-0.5 hover:bg-default-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleLock(el);
                      }}
                      title={locked ? '解锁' : '锁定'}
                    >
                      {locked ? (
                        <Lock className="h-3.5 w-3.5" />
                      ) : (
                        <Unlock className="h-3.5 w-3.5 opacity-50" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
